// src/utils/defaultPrompts.js

export const COMPLEX_TO_SIMPLE_QUERY_PROMPT = `""
    You are an AI language model assistant that is the master of query generation.
    Your Job is to take a complex query and deconstruct it into several simpler queries.
    Each simplified query should focus exclusively on one distinct concept found in the original query.
    Input Specification:
    Complex Query: A single, detailed query that might contain multiple intertwined concepts.
    Output Specification:
    Simple Queries: A list of queries where each query addresses a single, isolated concept from the complex query.
    Process:
    Identify Concepts: Analyze the complex query to identify distinct concepts. This involves parsing the query for keywords, phrases, choices, and their contextual dependencies. If a question is a multiple-choice question, consider each option as a standalone concept.
    Generate Queries: For each identified concept, generate a new query. Ensure that each new query:
    - Contains only elements relevant to its respective concept.
    - Is formulated clearly enough to be understood and processed independently.
    Always provide these new questions separated by newlines.
    Complex Query: {query}
""`;

export const QUERY_EXPANSION = `""
    You are an AI language model assistant that is the master of query generation.
    Your Job is to take a query and expand it into a more robust and detailed query.
    The detailed query should focus on each distinct concept found in the original query and explain it in a better and descriptive way.
    Input Specification:
    Input Query: A single, detailed query that might contain multiple intertwined concepts.
    Output Specification:
    Output Query: A detailed query that addresses all concepts from the Input query in a clearer and more descriptive fashion.
    Process:
    Identify Concepts: Analyze the Input query to identify distinct concepts. This involves parsing the query for keywords, phrases, choices, and their contextual dependencies. If a question is a multiple-choice question, consider each option as a standalone concept.
    Generate Queries: For each identified concept, expand it to be clearer and more detailed. Ensure that the new query:
    - Contains all the information in the Input query but in a more detailed fashion.
    - Is formulated clearly enough to be understood and processed.
    Simply return the new query.
    Input Query: {query}
    Output Query: <<Your new query>>
""`;

export const MULTI_QUERY_PROMPT = `""
    You are an AI language model assistant. Your task is to generate 3 different versions of the given user question to retrieve relevant documents from a vector database.
    By generating multiple perspectives on the user question, your goal is to help the user overcome some of the limitations of distance-based similarity search.
    Provide these alternative questions separated by newlines.
    Original question: {query}
""`;

export const HYDE_PROMPT = `""
    Answer the following query:
    Query: {query}
""`;

export const ITERATIVE_DECONSTRUCTION = `""Help break down complex problems into step-by-step search queries to gather all required information. Here's a structured process to follow:
1. If previous helper query and context is not provided, create the first helper query
2. If previous helper queries and contexts are provided, use the context to create the next helper query that targets missing information.
3. Up to 5 helper queries can be created.
4. Do not repeat previous queries.
5. If all data is found or 5 queries are reached, respond with "*stop*".
6. Respond in the following format and do not write anything else
    sub-query: <the created sub-query or *stop* goes here>
Notes:
Each helper query should maintain a logical progression towards the end goal
Avoid repeating previous queries
Only provide the next helper query - don't answer previous queries


Helper Query: {query}
Context: {context}

Already created helper queries: {all_queries}

Your Answer : <<helper_query>>
""`;

export const CONTEXTUAL_RETRIEVAL_PROMPT = `""
    <document>
    {complete_document}
    </document>

    Here is the chunk we want to situate within the whole document
    <chunk>
    {text}
    </chunk>

    Please give a short succinct context to situate this chunk within the overall document for the purposes of improving search retrieval of the chunk.
    Answer only with the succinct context and nothing else.
""`;
export const LLAMA_PARSE_PROMPT = `""
Parse the document and follow the guidelines given below
    Maintain headings:
    Every output should be under some markdown heading; document heading, section heading, paragraph heading, table heading. Do not miss the heading.
    Parse tables:
    Accurately represent tables in markdown format using '|' for new column
    Multiple tables per page:
    If the document contains multiple tables tiled  horizontally or vertically on a single page, parse each table individually.
    Hierarchical table structure:
    If any table has a hierarchical structure, preserve the hierarchical context in a flat Markdown table format. Attach the upper level category with the sub-category using '→' as a separator. For example, if category 'Primary residence' has two sub-categories  'Full Doc' and  'Alternate Doc' and these sub-categories have further two sub-categories 'Purch' and 'C/O', then the markdown category created for each column should be 'Primary Residence → Full Doc → Purch', 'Primary Residence → Full Doc → C/O', 'Primary Residence → Alternate Doc → Purch' and 'Primary Residence → Alternate Doc → C/O'
    Mathematical equations:
    If you encounter a mathematical equation, parse and represent accurately
    Integrity:
    Do not change the document or add anything from your own
""`;
export const TEXT_TO_NO_SQL_PROMPT = `""
Given a query you need to identify if this query can be performed on a mongoDB data
    A query will be answerable using mongoDB if any aggregation pipeline can be created using it.
    If the query is answerable using a mongoDB then return your answer in 'yes' else return 'no'

    Note : Only answer in yes or no

    query : {query}

    Your Answer : yes/no
""`;

export const KG_TRIPLET_EXTRACT_TMPL = `""
    -Goal-
        Given a text document, identify all entities and their entity types from the text and all relationships among the identified entities.
        Given the text, extract up to {max_knowledge_triplets} entity-relation triplets.

    -Steps-
        1. Identify all entities. For each identified entity, extract the following information:
            - entity_name: Name of the entity, capitalized
            - entity_type: Type of the entity
            - entity_description: Comprehensive description of the entity's attributes and activities
        Format each entity as ("entity"$$$$<entity_name>$$$$<entity_type>$$$$<entity_description>)

        2. From the entities identified in step 1, identify all pairs of (source_entity, target_entity) that are clearly related to each other.
        For each pair of related entities, extract the following information:
            - source_entity: name of the source entity, as identified in step 1
            - target_entity: name of the target entity, as identified in step 1
            - relation: relationship between source_entity and target_entity
            - relationship_description: explanation as to why you think the source entity and the target entity are related to each other

    Format each relationship as ("relationship"$$$$<source_entity>$$$$<target_entity>$$$$<relation>$$$$<relationship_description>)

    3. When finished, output.

    -Real Data-
    ######################
    text: {text}
    ######################
    output:
""`;
export const AGGRESSION_PROMPT = `"""
   You are a MongoDB expert. Your task is to generate a syntactically correct raw aggregation pipeline query based on the user's request.
    Schema Information:
    - *Table Schema:* {table_schema}
    - *Example Document:* {schema_description}
    Requirements:
    1. Return the raw aggregation pipeline in JSON format.
    - Format:"pipeline": "json_object"
    2. Limit the query to 10 results using the $limit stage unless the user specifies otherwise.
    3. Use the exact field names as they appear in the collection. Do not query for fields that do not exist.
    4. For date-related queries involving "today," use MongoDB's $currentDate, $dateFromString, or similar operators.
    5. *Avoid* using $trim or similar operators for conversions.
    6. *Do not* use $lookup in the pipeline.
    7. When querying string data types, consider using regex for efficient searches.
    *User Question:* {question}
"""`;
